import type { Article, InsightCategory } from '../types/content';

export const insightCategories: InsightCategory[] = [
  'Executive Leadership',
  'Recruitment Strategy',
  'Board Governance',
  'Talent Market Intelligence',
  'Career Development',
  'Hiring Trends',
  'Leadership Succession',
  'Workplace & Organizational Strategy',
];

export const articles: Article[] = [
  {
    slug: 'defining-success-before-the-search',
    title: 'Defining Success Before the Search Begins',
    category: 'Recruitment Strategy',
    excerpt: 'Why the most effective leadership searches start with agreement on outcomes, not a list of credentials.',
    date: '2026-10-15',
    readMinutes: 6,
    featured: true,
    sections: [
      {
        heading: 'Start with outcomes',
        paragraphs: [
          'Many searches begin with a job description assembled from past advertisements. A stronger starting point is a simple question: what must this person have achieved in their first eighteen months for the appointment to be considered a success?',
          'Answering that question forces stakeholders to agree on priorities early, and it gives the search team a clear lens for assessing every candidate.',
        ],
      },
      {
        heading: 'Align stakeholders early',
        paragraphs: [
          'Divergent expectations among board members, executives and peers are one of the most common reasons searches stall late in the process. Short, structured conversations with each stakeholder at the outset surface those differences while they are still easy to resolve.',
        ],
      },
      {
        heading: 'Turn outcomes into an assessment framework',
        paragraphs: [
          'Once outcomes are agreed, they can be translated into competencies, experiences and behaviours that interviewers assess consistently. This makes decisions fairer, more transparent and easier to explain to every candidate.',
        ],
      },
    ],
  },
  {
    slug: 'the-first-hundred-days-of-a-new-executive',
    title: 'The First Hundred Days of a New Executive',
    category: 'Executive Leadership',
    excerpt: 'How incoming leaders can listen, build trust and set direction without rushing to change.',
    date: '2026-10-08',
    readMinutes: 5,
    sections: [
      {
        heading: 'Listen before acting',
        paragraphs: [
          'New executives often feel pressure to demonstrate impact quickly. Yet the leaders who settle most successfully tend to invest their first weeks in understanding people, history and context.',
        ],
      },
      {
        heading: 'Build a small number of visible wins',
        paragraphs: [
          'Choosing two or three achievable early priorities helps establish credibility while longer-term strategy takes shape.',
          'These should be chosen with the team, not imposed upon it.',
        ],
      },
    ],
  },
  {
    slug: 'building-a-board-for-the-next-decade',
    title: 'Building a Board for the Next Decade',
    category: 'Board Governance',
    excerpt: 'Using a skills matrix to make each board appointment add capability rather than fill a seat.',
    date: '2026-09-29',
    readMinutes: 7,
    sections: [
      {
        heading: 'Map what the board will need',
        paragraphs: [
          'A skills matrix compares the board’s current expertise with the capabilities its strategy will demand. Gaps become the brief for future appointments.',
        ],
      },
      {
        heading: 'Balance experience with fresh perspective',
        paragraphs: [
          'Boards benefit from a mix of seasoned directors and newer voices who bring different professional backgrounds and ways of thinking.',
        ],
      },
    ],
  },
  {
    slug: 'reading-a-leadership-talent-market',
    title: 'Reading a Leadership Talent Market',
    category: 'Talent Market Intelligence',
    excerpt: 'What talent mapping reveals about competitors, availability and the shape of future leadership teams.',
    date: '2026-09-18',
    readMinutes: 6,
    sections: [
      {
        heading: 'Beyond a list of names',
        paragraphs: [
          'A good talent map shows more than who holds which role. It reveals how competitors structure their teams, where leadership depth sits, and which organisations consistently develop strong people.',
        ],
      },
      {
        heading: 'Using intelligence well',
        paragraphs: [
          'Market intelligence is most valuable when it informs decisions before a vacancy exists — shaping succession plans and organisational design.',
        ],
      },
    ],
  },
  {
    slug: 'preparing-for-an-executive-career-move',
    title: 'Preparing for an Executive Career Move',
    category: 'Career Development',
    excerpt: 'Practical steps senior professionals can take to approach their next move with clarity and discretion.',
    date: '2026-09-10',
    readMinutes: 5,
    sections: [
      {
        heading: 'Clarify what you want next',
        paragraphs: [
          'Before engaging with search firms, take time to articulate the scope, culture and challenges that would make a new role genuinely worthwhile.',
        ],
      },
      {
        heading: 'Tell a clear story',
        paragraphs: [
          'A concise narrative of your leadership — the situations you inherited, the decisions you made and the outcomes that followed — is more persuasive than a list of responsibilities.',
        ],
      },
    ],
  },
  {
    slug: 'what-candidates-value-in-a-hiring-process',
    title: 'What Candidates Value in a Hiring Process',
    category: 'Hiring Trends',
    excerpt: 'Clarity, respect and timely feedback remain the foundations of a process senior candidates trust.',
    date: '2026-08-28',
    readMinutes: 4,
    sections: [
      {
        heading: 'Transparency matters',
        paragraphs: [
          'Senior candidates are typically evaluating an opportunity as carefully as they are being evaluated. Clear information about the role, the process and the timeline helps them engage seriously.',
        ],
      },
      {
        heading: 'Feedback as a courtesy',
        paragraphs: [
          'Timely, specific feedback — even when the answer is no — leaves candidates with a positive impression of the organisation.',
        ],
      },
    ],
  },
  {
    slug: 'succession-planning-without-the-shelf-document',
    title: 'Succession Planning Without the Shelf Document',
    category: 'Leadership Succession',
    excerpt: 'Turning succession plans into living conversations that genuinely prepare future leaders.',
    date: '2026-08-14',
    readMinutes: 6,
    sections: [
      {
        heading: 'Make it a living process',
        paragraphs: [
          'Succession plans often sit untouched between annual reviews. Regular conversations about readiness and development keep them relevant.',
        ],
      },
      {
        heading: 'Combine internal and external views',
        paragraphs: [
          'Benchmarking internal successors against the external market gives boards confidence that they are making informed choices.',
        ],
      },
    ],
  },
  {
    slug: 'designing-leadership-teams-for-hybrid-work',
    title: 'Designing Leadership Teams for Hybrid Work',
    category: 'Workplace & Organizational Strategy',
    excerpt: 'How organisational design and leadership behaviours are adapting to distributed teams.',
    date: '2026-07-30',
    readMinutes: 5,
    sections: [
      {
        heading: 'Intentional connection',
        paragraphs: [
          'Distributed teams depend on leaders who create deliberate moments of connection and communicate with unusual clarity.',
        ],
      },
      {
        heading: 'Hiring for new behaviours',
        paragraphs: [
          'When recruiting leaders for hybrid organisations, it helps to explore how candidates have built trust and accountability across distance.',
        ],
      },
    ],
  },
];
